import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { formatUsd } from "@/lib/marketplace/queries";
import { decideProposalFn, getBuyerRequest, sendProposal } from "@/lib/marketplace/community.functions";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/request/$requestId")({
  head: () => ({
    meta: [
      { title: "Request detail | BeHot.Now Marketplace" },
      { name: "description", content: "Review a buyer request and its seller proposals on BeHot.Now Marketplace." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: RequestDetailPage,
});

function RequestDetailPage() {
  const { t } = useT();
  const { requestId } = Route.useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState({ message: "", price: "", days: "7" });

  const { data, isLoading } = useQuery({
    queryKey: ["mp-request", requestId, user?.id ?? null],
    enabled: Boolean(user),
    queryFn: () => getBuyerRequest({ data: { requestId } }),
  });

  const propose = useMutation({
    mutationFn: () =>
      sendProposal({
        data: {
          requestId,
          message: form.message,
          priceCents: Math.round(Number(form.price) * 100),
          deliveryDays: Number(form.days) || 7,
        },
      }),
    onSuccess: () => {
      toast.success(t("mp.request.toast_proposal_sent"));
      setForm({ message: "", price: "", days: "7" });
      void qc.invalidateQueries({ queryKey: ["mp-request", requestId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("mp.request.toast_proposal_error")),
  });

  const decide = useMutation({
    mutationFn: (v: { proposalId: string; decision: "accepted" | "declined" }) => decideProposalFn({ data: v }),
    onSuccess: (res) => {
      toast.success(res.offerId ? t("mp.request.toast_accepted") : t("mp.request.toast_declined"));
      void qc.invalidateQueries({ queryKey: ["mp-request", requestId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("mp.request.toast_decide_error")),
  });

  if (isLoading) {
    return (
      <MarketplaceGate>
        <div className="mx-auto max-w-3xl px-4 py-10">
          <Skeleton className="h-40 w-full" />
        </div>
      </MarketplaceGate>
    );
  }
  if (!data) return null;
  const { request, isOwner, proposals } = data;

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="font-display text-2xl font-bold">{request.title}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="secondary">
            {formatUsd(request.budget_min_cents)} – {formatUsd(request.budget_max_cents)}
          </Badge>
          <span>{request.status}</span>
          {request.delivery_days ? <span>· {t("mp.order.days", { n: request.delivery_days })}</span> : null}
        </div>
        <p className="mt-4 whitespace-pre-wrap text-sm">{request.description}</p>

        {!isOwner && request.status === "open" && (
          <div className="card-surface mt-8 space-y-3 p-5">
            <h2 className="font-display text-lg font-bold">{t("mp.request.send_proposal")}</h2>
            <p className="text-xs text-muted-foreground">{t("mp.request.verified_sellers_only")}</p>
            <Textarea
              rows={4}
              placeholder={t("mp.request.proposal_placeholder")}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                type="number"
                placeholder={t("mp.request.price_placeholder")}
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
              <Input
                type="number"
                placeholder={t("mp.request.days_placeholder")}
                value={form.days}
                onChange={(e) => setForm({ ...form, days: e.target.value })}
              />
            </div>
            <Button variant="hot" disabled={propose.isPending} onClick={() => propose.mutate()}>
              {propose.isPending ? t("mp.request.sending") : t("mp.request.send_proposal")}
            </Button>
          </div>
        )}

        <h2 className="mt-10 font-display text-lg font-bold">{t("mp.request.proposals_count", { n: proposals.length })}</h2>
        <ul className="mt-4 space-y-3">
          {proposals.map((p) => (
            <li key={p.id} className="card-surface p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{p.seller?.display_name || p.seller?.handle || t("mp.common.seller")}</p>
                <Badge variant="secondary">
                  {formatUsd(p.price_cents)} · {t("mp.order.days", { n: p.delivery_days })}
                </Badge>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{p.message}</p>
              <p className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">{p.status}</p>
              {isOwner && p.status === "sent" && (
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    variant="hot"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ proposalId: p.id, decision: "accepted" })}
                  >
                    {t("mp.request.accept")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ proposalId: p.id, decision: "declined" })}
                  >
                    {t("mp.request.decline")}
                  </Button>
                </div>
              )}
            </li>
          ))}
          {proposals.length === 0 && <p className="text-sm text-muted-foreground">{t("mp.request.no_proposals")}</p>}
        </ul>
      </div>
    </MarketplaceGate>
  );
}
